'use client';

import React, { useCallback, useMemo, useState } from 'react';
import {
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	PRICE_PRECISION_EXP,
	ZERO,
	PerpMarketAccount,
} from '@drift-labs/sdk';
import { usePathname } from 'next/navigation';
import NumLib from 'src/utils/NumLib';
import { roundToStepSizeIfLargeEnough } from 'src/utils/math';
import useCurrentSettings from 'src/hooks/useCurrentSettings';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useDriftStore, {
	DEFAULT_MARKET_CONFIG,
} from 'src/stores/DriftStore/useDriftStore';
import useTradeFormInputStateEngine from '../hooks/useTradeFormInputStateEngine';
import { usePerpTradeFormStateEngine } from '../hooks/usePerpTradeFormStateEngine';
import useOraclePrice from 'src/hooks/useOraclePrice';
import useMaxMarginRatioAndLeverage from 'src/hooks/useMaxMarginRatioAndLeverage';
import useInfoForCurrentlySelectedMarket from 'src/hooks/useInfoForCurrentlySelectedMarket';
import usePrePreppedTxStore from 'src/stores/useTxDataStore';
import {
	useSetTradeFormPriceBox,
	useSetTradeFormSecondaryPriceBox,
} from 'src/components/TradeForm/hooks/useSetTradeFormInputState';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import useMarkPrice from 'src/hooks/useMarkPrice';
import { getLimitPriceVsTriggerPriceWarning } from 'src/utils/trade';
import useHighLeverageSpotsOpen from 'src/hooks/useHighLeverageSpotsOpen';
import { SaveTpSlOrdersCallback } from 'src/components/TradeForm/TpSlOrderForm/types';
import { useSwiftServerHealthCheck } from 'src/hooks/swift/useSwiftServerHealthcheck';
import { useIsSignedMsgUserOrdersAccountInitialized } from 'src/hooks/signedMsgOrders/useIsSignedMsgUserOrdersAccountInitialized';
import { PerpTradeFormContent } from './PerpTradeFormContent';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import {
	COMMON_UI_UTILS,
	computeIsolatedPositionDepositForTrade,
	getOtherIsolatedMarginShortfalls,
	AdditionalIsolatedPositionDeposit,
	MarketId,
	UIMarket,
	UIOrderType,
} from '@drift/common';
import { ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR } from 'src/constants/trade';
import { useOpenPerpOrder } from 'src/hooks/trade/perp/useOpenPerpOrder';
import useModal from 'src/components/Modals/hooks/useModal';
import useMarketSettingsStore from 'src/stores/useMarketSettingsStore';
import useDriftClient from 'src/hooks/useDriftClient';
import useTradeFormHandleSizeInputChange from 'src/components/TradeForm/hooks/useTradeFormHandleSizeInputChange';
import { useCrossMarginValidationForIsolatedDeposit } from '../hooks/useCrossMarginValidationForIsolatedDeposit';

export const PerpTradeForm = () => {
	/***************************************************************/
	/** Non-Trading Related Hooks **********************************/
	/***************************************************************/

	const pathname = usePathname();
	const connected = useWalletIsConnected();
	const [savedSettings] = useCurrentSettings();
	const { captureEvent } = usePostHogCapture();
	const set = useDriftStore((s) => s.set);
	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);
	const { show, hide } = useModal();

	/***************************************************************/
	/** Trading Related Hooks **************************************/
	/***************************************************************/

	const openPerpOrder = useOpenPerpOrder();
	const selectedMarketInfo = useInfoForCurrentlySelectedMarket();
	const maxMarginRatio = useMaxMarginRatioAndLeverage()[0];
	const prePreparedTx = usePrePreppedTxStore((s) => s.preppedPerpTradeformTxs);

	const setPriceBoxStringValue = useSetTradeFormPriceBox();
	const setSecondaryPriceBoxStringValue = useSetTradeFormSecondaryPriceBox();

	// This hook keeps the input values in the drift store in sync
	useTradeFormInputStateEngine();
	// useSyncPrePreparedPerpTradeFormOrder(); // pre-prepped txs aren't being used in useOpenPerpOrder at the moment - main reason is that since SWIFT is the dominant way to place market/limit orders and SWIFT is fast, we don't need to prep the txns anymore

	/***************************************************************/
	/** SWIFT ******************************************************/
	/***************************************************************/

	const {
		data: isSignedMsgUserOrdersAccountInitialized,
		isLoading: isLoadingIsSignedMsgUserOrdersAccountInitialized,
	} = useIsSignedMsgUserOrdersAccountInitialized();
	const isSwiftServerHealthy = useSwiftServerHealthCheck();
	const currentUserAccount = useDriftAccountStore((s) =>
		s.getCurrentUserAccount()
	);
	const userAccountExists = !!currentUserAccount;
	const shouldShowSwiftEnableMessage =
		!!userAccountExists &&
		!isLoadingIsSignedMsgUserOrdersAccountInitialized &&
		!isSignedMsgUserOrdersAccountInitialized &&
		isSwiftServerHealthy;

	/***************************************************************/
	/** High Leverage Mode *****************************************/
	/***************************************************************/

	const { highLeverageSpotsOpen } = useHighLeverageSpotsOpen();

	/***************************************************************/
	/** UI States **************************************************/
	/***************************************************************/

	const [isSendingTransaction, setIsSendingTransaction] = useState(false);
	const [hasAgreedToPriceDivergence, setHasAgreedToPriceDivergence] =
		useState(false);

	/***************************************************************/
	/** Trade States ***********************************************/
	/***************************************************************/

	const selectedUiMarket = useDriftStore((s) => s.selectedMarket.current);
	const selectedMarketId = selectedUiMarket?.marketId;
	const markPrice = useMarkPrice(selectedMarketId);
	const oraclePrice = useOraclePrice(selectedMarketId as MarketId);

	const tradeFormState = useDriftStore((s) => s.tradeForm);
	const tradeFormDerivedState = usePerpTradeFormStateEngine();

	const {
		side,
		quoteSizeStringValue,
		baseSizeStringValue,
		reduceOnly,
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
		leverageAfterTrade,
	} = tradeFormState;

	const selectedMarketSymbol = selectedUiMarket?.symbol;
	const selectedMarketBaseAssetSymbol = selectedUiMarket?.baseAssetSymbol();

	const marginInfo = useCurrentAccountMarginInfo();
	const currentLeverage = marginInfo?.leverage ?? 0;
	const totalCollateral = marginInfo?.totalCollateral ?? ZERO;
	const freeCollateral = marginInfo?.freeCollateral ?? ZERO;
	const totalCurrentPositionQuoteSizeNum = NumLib.formatBn.fromQuote(
		totalCollateral
			.sub(freeCollateral)
			.mul(new BN(tradeFormDerivedState.maxLeverageAvailable))
	);

	const inputQuoteSizeNum = Number(quoteSizeStringValue);
	const inputBaseSizeBigNum = useMemo(
		() => BigNum.fromPrint(baseSizeStringValue.toString(), BASE_PRECISION_EXP),
		[baseSizeStringValue]
	);

	const currentPositionQuoteSizeNum = NumLib.formatBn.fromQuote(
		tradeFormDerivedState.currentPosition?.quoteAssetNotionalAmount ?? ZERO
	);
	const currentPositionBaseSizeBigNum = useMemo(
		() =>
			BigNum.from(
				(tradeFormDerivedState.currentPosition?.baseSize ?? ZERO).abs(),
				BASE_PRECISION_EXP
			),
		[tradeFormDerivedState.currentPosition?.baseSize]
	);

	const { getMarginModeForMarket } = useMarketSettingsStore();
	const userKey = currentUserAccount?.userKey;
	const marginMode = getMarginModeForMarket(
		selectedUiMarket?.marketId.key,
		userKey ?? ''
	);
	const driftClient = useDriftClient();

	const isolatedPositionDeposit = useMemo(() => {
		if (
			marginMode === 'isolated' &&
			driftClient &&
			currentUserAccount?.client
		) {
			return (
				computeIsolatedPositionDepositForTrade({
					driftClient,
					user: currentUserAccount.client,
					marketIndex:
						selectedUiMarket?.marketIndex ?? DEFAULT_MARKET_CONFIG.marketIndex,
					baseAssetAmount: inputBaseSizeBigNum.val,
					marginRatio:
						COMMON_UI_UTILS.convertLeverageToMarginRatio(
							tradeFormDerivedState.displayLeverage ?? 1
						) ?? 1,
					numOfOpenHighLeverageSpots: highLeverageSpotsOpen,
					bufferDenominator: ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR,
					includeExistingShortfall: true,
				}) ?? undefined
			);
		}
	}, [
		inputBaseSizeBigNum,
		selectedMarketId?.marketIndex,
		marginMode,
		!!currentUserAccount?.client,
		tradeFormDerivedState.displayLeverage,
	]);

	// Compute additional isolated position deposits for under-collateralized positions
	const additionalIsolatedPositionDeposits = useMemo(():
		| AdditionalIsolatedPositionDeposit[]
		| undefined => {
		if (!currentUserAccount?.client) {
			return undefined;
		}

		// Get shortfalls from other isolated positions (excluding the current market)
		const otherShortfalls = getOtherIsolatedMarginShortfalls(
			currentUserAccount.client,
			selectedUiMarket?.marketIndex
		);

		if (!otherShortfalls.length) {
			return undefined;
		}

		return otherShortfalls.map((shortfall) => {
			// Add 5% buffer on top of shortfall (same as new margin)
			const shortfallWithBuffer = shortfall.shortfall.add(
				shortfall.shortfall.div(
					new BN(ISOLATED_POSITION_DEPOSIT_BUFFER_DENOMINATOR)
				)
			);
			return {
				marketIndex: shortfall.marketIndex,
				amount: shortfallWithBuffer,
			};
		});
	}, [currentUserAccount?.client, selectedUiMarket?.marketIndex]);

	// Compute total margin required including all isolated shortfalls
	const totalMarginRequired = useMemo(() => {
		if (!currentUserAccount?.client) {
			return isolatedPositionDeposit;
		}

		// For isolated mode, the isolatedPositionDeposit already includes the current market's shortfall with buffer
		// Sum up additional deposits (which already include buffers) for other markets
		const otherShortfallsTotal =
			additionalIsolatedPositionDeposits?.reduce(
				(acc, deposit) => acc.add(deposit.amount),
				ZERO
			) ?? ZERO;

		if (isolatedPositionDeposit && otherShortfallsTotal.gt(ZERO)) {
			return isolatedPositionDeposit.add(otherShortfallsTotal);
		}

		if (otherShortfallsTotal.gt(ZERO)) {
			return otherShortfallsTotal;
		}

		return isolatedPositionDeposit;
	}, [
		isolatedPositionDeposit,
		additionalIsolatedPositionDeposits,
		currentUserAccount?.client,
	]);
	const crossMarginError = useCrossMarginValidationForIsolatedDeposit({
		totalMarginRequired,
		freeCollateral,
		marginMode: marginMode ?? 'cross',
	});

	/**
	 * Ensures we only submit bracket orders for market and limit orders.
	 */
	const getBracketOrdersToSubmit = useCallback(() => {
		return ['market', 'limit'].includes(tradeFormState.orderType)
			? tradeFormState.bracketOrders
			: undefined;
	}, [tradeFormState.orderType, tradeFormState.bracketOrders]);

	const outputParams = useMemo(
		() => ({
			...tradeFormState,
			bracketOrders: getBracketOrdersToSubmit(),
			targetMarketIndex:
				selectedMarketId?.marketIndex ?? DEFAULT_MARKET_CONFIG.marketIndex,
			oraclePrice: oraclePrice?.val,
			markPrice: markPrice?.val,
			perpMarketAccount: selectedMarketInfo?.info?.account as PerpMarketAccount,
			displayLeverage: tradeFormDerivedState.displayLeverage,
			isolatedPositionDeposit,
			additionalIsolatedPositionDeposits,
		}),
		[
			tradeFormState,
			selectedMarketId?.marketIndex,
			oraclePrice?.val,
			markPrice?.val,
			selectedMarketInfo?.info?.account,
			tradeFormDerivedState.displayLeverage,
			isolatedPositionDeposit,
			additionalIsolatedPositionDeposits,
			getBracketOrdersToSubmit,
		]
	);

	const hasNoCollateral = useDriftAccountStore(
		(s) =>
			!s.getCurrentUserAccount() ||
			s
				.getCurrentUserAccount()
				?.spotBalances.every((spotBalance) => spotBalance.balance.eqZero())
	);

	const entryPriceForTpSl = useMemo(() => {
		if (tradeFormState.orderType === 'limit') {
			return BigNum.fromPrint(priceBoxStringValue, PRICE_PRECISION_EXP);
		}

		return BigNum.from(
			tradeFormState.priceImpact?.entryPrice,
			PRICE_PRECISION_EXP
		);
	}, [
		tradeFormState.orderType,
		priceBoxStringValue,
		oraclePrice,
		tradeFormState.priceImpact?.entryPrice,
	]);

	/***************************************************************/
	/** Component States *******************************************/
	/***************************************************************/

	const isSliderDisabled = !connected;

	const showTradeConfirmation = savedSettings.showTradeConfirmation;

	const isTradeButtonDisabled =
		tradeFormDerivedState.tradeButtonIsDisabled ||
		tradeFormState.bracketOrders?.anyInvalid ||
		(tradeFormDerivedState.showPriceEstimateOracleDivergenceWarning &&
			!showTradeConfirmation &&
			!hasAgreedToPriceDivergence) ||
		(isGeoblocked && !reduceOnly) ||
		isSendingTransaction ||
		crossMarginError?.disableTradeButton;

	// if user does not have current position, it is considered to be the same side
	const isSameSideAsCurrentPosition =
		!tradeFormDerivedState.currentPosition?.baseSize ||
		tradeFormDerivedState.currentPosition.baseSize.eq(ZERO) ||
		(tradeFormDerivedState.currentPosition.baseSize.isNeg()
			? 'sell'
			: 'buy') === side;

	const isTriggerLimit =
		tradeFormState.orderType === 'stopLimit' ||
		tradeFormState.orderType === 'takeProfitLimit';
	const limitPriceVsTriggerPriceWarning = isTriggerLimit
		? getLimitPriceVsTriggerPriceWarning(
				BigNum.fromPrint(priceBoxStringValue, PRICE_PRECISION_EXP).val,
				BigNum.fromPrint(secondaryPriceBoxStringValue, PRICE_PRECISION_EXP).val,
				side === 'buy' ? 'long' : 'short',
				false
			)
		: null;

	/***************************************************************/
	/** Callbacks **************************************************/
	/***************************************************************/

	const setCurrentOrderType = useCallback(
		(orderType: UIOrderType) =>
			set((s) => {
				s.tradeForm.orderType = orderType;
			}),
		[set]
	);

	const togglePostOnly = useCallback(() => {
		set((s) => {
			s.tradeForm.postOnly = !s.tradeForm.postOnly;
		});
	}, [set]);

	const toggleIsTpSlCheckboxChecked = useCallback(() => {
		set((s) => {
			s.tradeForm.isTpSlCheckboxChecked = !s.tradeForm.isTpSlCheckboxChecked;
		});
	}, [set]);

	const saveBracketOrdersCallback: SaveTpSlOrdersCallback = useCallback(
		(bracketOrders) => {
			set((s) => {
				s.tradeForm.bracketOrders = bracketOrders;
			});
		},
		[set]
	);

	const onSubmit = useCallback(async () => {
		try {
			// Hardcode disable after submit.
			setIsSendingTransaction(true);
			setTimeout(() => {
				setIsSendingTransaction(false);
			}, 3000);

			if (!connected && pathname) {
				set((s) => {
					s.wallet.lastRouteBeforeConnect = pathname;
				});
				show({ type: 'connect_wallet', props: {} });
				return;
			}

			if (hasNoCollateral) {
				show({ type: 'deposit', props: {} });
				return;
			}

			if (tradeFormDerivedState.tradeButtonIsDisabled) {
				setIsSendingTransaction(false);
				return;
			}

			if (showTradeConfirmation) {
				show({
					type: 'trade_confirmation',
					props: {
						tradeFormDetails: {
							...tradeFormState,
							bracketOrders: getBracketOrdersToSubmit(),
							market: selectedMarketId as MarketId,
						},
					},
				});
				return;
			} else {
				hide();
				const result = await openPerpOrder(outputParams, highLeverageSpotsOpen);

				if (result) {
					set((s) => {
						s.tradeForm.bracketOrders = undefined;
					});
				}
			}
		} finally {
			//
		}
	}, [
		setIsSendingTransaction,
		set,
		captureEvent,
		openPerpOrder,
		pathname,
		connected,
		hasNoCollateral,
		tradeFormDerivedState.tradeButtonIsDisabled,
		getBracketOrdersToSubmit,
		showTradeConfirmation,
		outputParams,
		tradeFormState,
		selectedMarketId,
		selectedMarketSymbol,
		prePreparedTx,
		highLeverageSpotsOpen,
		hide,
		show,
	]);

	const setSide = useCallback(
		(side: 'buy' | 'sell') => {
			set((s) => {
				s.tradeForm.side = side;
			});
		},
		[set]
	);

	const handleSizeInputChange = useTradeFormHandleSizeInputChange({
		stepSize: tradeFormDerivedState.stepSize,
	});

	const toggleReduceOnly = useCallback(() => {
		const newReduceOnly = !tradeFormState.reduceOnly;
		const shouldUpdateBaseSize =
			newReduceOnly && inputBaseSizeBigNum.gt(currentPositionBaseSizeBigNum);

		set((s) => {
			s.tradeForm.reduceOnly = newReduceOnly;

			if (shouldUpdateBaseSize) {
				const rounded = roundToStepSizeIfLargeEnough(
					currentPositionBaseSizeBigNum.toTradePrecision(),
					tradeFormDerivedState.stepSize
				);
				// @ts-expect-error
				s.tradeForm.baseSizeStringValue = rounded;
			}

			if (newReduceOnly) {
				s.tradeForm.isTpSlCheckboxChecked = false;
			}
		});

		if (shouldUpdateBaseSize) {
			tradeFormDerivedState.handleFormChange();
		}
	}, [
		tradeFormState.reduceOnly,
		inputBaseSizeBigNum,
		currentPositionBaseSizeBigNum,
		tradeFormDerivedState.stepSize,
		tradeFormDerivedState.handleFormChange,
		set,
	]);

	const handleSliderChange = useCallback(
		(v: number) => {
			const newValue = Math.floor(v * 10000) / 10000;

			// If we've slid the value down to `0`, clear the input.
			const valueToPass = !newValue ? '' : newValue.toFixed(2);

			handleSizeInputChange(valueToPass, {
				// The leverage slider only operates on the quote size.
				sizeUnit: 'quote',
			});
		},
		[handleSizeInputChange]
	);

	const handleSizeDistributionChange = useCallback(
		(value: 'flat' | 'ascending' | 'descending') =>
			set((s) => {
				s.tradeForm.scaledOrders.sizeDistribution = value;
			}),
		[]
	);

	const handleInputSourceChange = useCallback(
		(inputSource: 'base' | 'quote') => {
			set((s) => {
				s.tradeForm.leadSide = inputSource;
			});
		},
		[set]
	);

	const saveLimitPrice = useCallback(
		(value: string) => {
			if (tradeFormState.orderType !== 'limit') return;
			set((s) => {
				s.tradeForm.savedLimitPrice = isNaN(Number(value))
					? null
					: {
							market: selectedMarketId as MarketId,
							price: Number(value),
						};
			});
		},
		[tradeFormState.orderType, selectedMarketId?.marketIndex]
	);

	// slider stays focused when mouse leaves the tradeform while dragging - this fixes by unfocusing it when you re-enter
	const unFocus = useCallback(() => {
		//@ts-ignore
		if (document.activeElement.role === 'slider') {
			//@ts-ignore
			document.activeElement.blur();
		}
	}, []);

	return (
		<PerpTradeFormContent
			selectedUiMarket={selectedUiMarket as UIMarket}
			tradeFormDerivedState={tradeFormDerivedState}
			tradeFormStoreState={tradeFormState}
			priceBoxStringValue={priceBoxStringValue}
			formattedMarketSymbol={selectedMarketBaseAssetSymbol ?? ''}
			currentPositionBaseSize={currentPositionBaseSizeBigNum}
			rawBaseValue={inputBaseSizeBigNum}
			rawQuoteValue={inputQuoteSizeNum}
			totalCurrentPositionSize={totalCurrentPositionQuoteSizeNum}
			currentPositionSize={currentPositionQuoteSizeNum}
			isSameSideAsCurrentPosition={isSameSideAsCurrentPosition}
			hasAgreedToPriceDivergence={hasAgreedToPriceDivergence}
			limitPriceVsTriggerPriceWarning={limitPriceVsTriggerPriceWarning ?? ''}
			currentMarketSymbol={selectedMarketSymbol ?? ''}
			hasNoCollateral={hasNoCollateral ?? false}
			maxMarginRatio={maxMarginRatio}
			secondaryPriceBoxStringValue={secondaryPriceBoxStringValue}
			baseSizeStringValue={baseSizeStringValue}
			quoteSizeStringValue={quoteSizeStringValue}
			currentLeverage={currentLeverage}
			leverageAfterTrade={leverageAfterTrade}
			entryPriceForTpSl={entryPriceForTpSl}
			// Component Display Conditions
			isTradeButtonDisabled={isTradeButtonDisabled ?? false}
			totalMarginRequired={totalMarginRequired}
			crossMarginErrorMessage={crossMarginError?.message}
			showTradeConfirmation={showTradeConfirmation}
			isSliderDisabled={isSliderDisabled}
			shouldShowSwiftEnableMessage={shouldShowSwiftEnableMessage ?? false}
			// Callbacks
			unFocus={unFocus}
			onSubmit={onSubmit}
			setHasAgreedToPriceDivergence={setHasAgreedToPriceDivergence}
			onSizeInputChange={handleSizeInputChange}
			onSliderChange={handleSliderChange}
			onDirectionChange={setSide}
			onOrderTypeChange={setCurrentOrderType}
			onPriceBoxChange={setPriceBoxStringValue}
			onSecondaryPriceBoxChange={setSecondaryPriceBoxStringValue}
			onSizeDistributionChange={handleSizeDistributionChange}
			onInputSourceChange={handleInputSourceChange}
			saveLimitPrice={saveLimitPrice}
			toggleReduceOnly={toggleReduceOnly}
			togglePostOnly={togglePostOnly}
			toggleIsTpSlCheckboxChecked={toggleIsTpSlCheckboxChecked}
			saveBracketOrdersCallback={saveBracketOrdersCallback}
		/>
	);
};
